import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore, authCallbackPath, hasAuthCallbackQuery } from '@/stores/useAuthStore'
import { isAuth0Configured } from '@/config/assistancePortalConfig'
import { i18n } from '@/i18n'

function routeRequiresAuth(to: RouteLocationNormalized) {
  return to.matched.some((record) => record.meta.requiresAuth)
}

export var router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior() {
    return { top: 0, left: 0 }
  },
  routes: [
    {
      path: '/',
      component: () => import('@/layouts/MainLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'my-open-claims',
          component: () => import('@/views/MyOpenClaimsView.vue'),
          meta: { titleKey: 'ClaimPortalTitle' },
        },
        {
          path: 'file-a-claim/select-policy',
          name: 'select-policy',
          component: () => import('@/views/SelectPolicyView.vue'),
          meta: { titleKey: 'SelectPolicyTitle' },
        },
        {
          path: 'file-a-claim/:policyId/intake',
          name: 'claim-intake',
          component: () => import('@/views/ClaimIntakeChatView.vue'),
          props: true,
          meta: { titleKey: 'ClaimIntakeTitle' },
        },
        {
          path: 'file-a-claim/:claimId/upload-claim-forms',
          name: 'upload-claim-forms',
          component: () => import('@/views/ClaimUploadFormsView.vue'),
          props: (route) => ({ claimId: route.params.claimId, showValidation: false }),
          meta: { titleKey: 'UploadClaimFormsTitle' },
        },
        {
          path: 'my-claims/:claimId/status',
          name: 'claim-status',
          component: () => import('@/views/ClaimUploadFormsView.vue'),
          props: (route) => ({ claimId: route.params.claimId, showValidation: true }),
          meta: { titleKey: 'UploadClaimFormsTitle' },
        },
        {
          path: 'file-a-claim/:claimId/upload-claim-documents',
          name: 'upload-claim-documents',
          component: () => import('@/views/ClaimUploadDocumentsView.vue'),
          props: (route) => ({ claimId: route.params.claimId, showValidation: false }),
          meta: { titleKey: 'UploadClaimDocumentsTitle' },
        },
        {
          path: 'my-claims/:claimId/documents-status',
          name: 'claim-documents-status',
          component: () => import('@/views/ClaimUploadDocumentsView.vue'),
          props: (route) => ({ claimId: route.params.claimId, showValidation: true }),
          meta: { titleKey: 'UploadClaimDocumentsTitle' },
        },
        {
          path: 'file-a-claim/:claimId/claim-submission-confirmation',
          name: 'claim-submission-confirmation',
          component: () => import('@/views/ClaimSubmitConfirmationView.vue'),
          props: true,
          meta: { titleKey: 'ClaimSubmitConfirmationTitle' },
        },
        {
          path: 'my-open-claims/:claimId/status',
          name: 'claim-review-status',
          component: () => import('@/views/ClaimReviewStatusView.vue'),
          props: true,
          meta: { titleKey: 'ClaimPortalTitle' },
        },
      ],
    },
    {
      path: authCallbackPath,
      name: 'auth-callback',
      component: () => import('@/views/AuthCallbackView.vue'),
      meta: { allowAnonymous: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { allowAnonymous: true, titleKey: 'Login' },
    },
    {
      path: '/set-culture/:culture',
      name: 'set-culture',
      component: () => import('@/views/SetCultureView.vue'),
      props: true,
      meta: { allowAnonymous: true },
    },
    // Legacy login path used by Auth0 password-change ResultUrl.
    {
      path: '/Account/Login',
      redirect: (to) => ({ path: '/login', query: to.query }),
    },
    {
      path: '/error',
      name: 'error',
      component: () => import('@/views/ErrorView.vue'),
      meta: { allowAnonymous: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
      meta: { allowAnonymous: true },
    },
  ],
})

router.afterEach((to) => {
  var titleKey = to.meta.titleKey as string | undefined
  if (titleKey) {
    document.title = i18n.global.t(titleKey)
  }
})

router.beforeEach(async (to) => {
  var authStore = useAuthStore()
  if (to.path === authCallbackPath) {
    if (hasAuthCallbackQuery()) {
      return await authStore.handleAuthCallback()
    }
    return '/'
  }
  if (!authStore.initialized) {
    await authStore.init()
  }
  if (routeRequiresAuth(to) && !authStore.isAuthenticated) {
    if (!isAuth0Configured()) {
      return false
    }
    await authStore.login(to.fullPath)
    return false
  }
})
